import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UsersThree, Plus, SignIn, Copy, Trash, ShareNetwork, ArrowsLeftRight, X, Check, Image as ImageIcon, DeviceMobile, DesktopTower, CalendarBlank, Archive, ArrowCounterClockwise, Eye, ArrowSquareOut } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shell } from "@/components/Shell";
import { PeopleManager } from "@/components/PeopleManager";
import { FamilyManager } from "@/components/FamilyManager";
import { useApp } from "@/context/AppContext";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { copyText } from "@/lib/clipboard";
import api from "@/lib/api";

const STATUS_META = {
  active: { label: "Aktiv", color: "#22C55E" },
  shared: { label: "Freigegeben", color: "var(--app-primary)" },
  managed: { label: "In Verwaltung", color: "#0EA5E9" },
  transferred: { label: "Übertragen", color: "#6B7280" },
};

const FIELD_OPTIONS = [
  ["label", "Bezeichnung"], ["display_name", "Anzeigename"], ["avatar", "Avatar"],
  ["contacts", "Kontakte"], ["notes", "Notizen"], ["use_default_profile", "Profildaten-Quelle"],
  ["allow_calls", "Anrufe"], ["allow_messages", "Nachrichten"],
];

const GROUP_STATUS = {
  active: { label: "Aktiv", color: "#22C55E" },
  upcoming: { label: "Bevorstehendes Event", color: "#F59E0B" },
  inactive: { label: "Archiviert", color: "#0EA5E9" },
  expired: { label: "Abgelaufen", color: "#EF4444" },
};

const GStatus = ({ status }) => {
  const m = GROUP_STATUS[status] || GROUP_STATUS.active;
  return <span data-testid={`group-status-${status}`} className="inline-flex items-center gap-1 text-[10px] font-heading px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: m.color }}><span className="w-1.5 h-1.5 rounded-full bg-white/90" />{m.label}</span>;
};

const fmtRange = (s, e) => {
  const f = (x) => x ? new Date(x).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "?";
  return `${f(s)} – ${f(e)}`;
};

function GroupCard({ g, onClick }) {
  return (
    <button data-testid={`group-item-${g.id}`} onClick={onClick} className="sb-card w-full p-4 text-left active:scale-[0.99] transition-transform">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-heading font-semibold truncate">{g.name}</p>
          <p className="font-body text-xs text-gray-500 mt-0.5">{g.kind === "event" ? "Event" : "Standard"} · {g.member_count} Mitglieder{typeof g.buddy_count === "number" ? ` · ${g.buddy_count} Buddies` : ""} · {g.is_leader ? "Leiter" : "Mitglied"}</p>
          {g.kind === "event" && (g.start_at || g.end_at) && <p className="font-body text-xs text-gray-400 mt-0.5 flex items-center gap-1"><CalendarBlank size={12} weight="bold" />{fmtRange(g.start_at, g.end_at)}</p>}
        </div>
        <GStatus status={g.status} />
      </div>
    </button>
  );
}

const BUDDY_STATUS = {
  active: { label: "Aktiv", color: "#22C55E" },
  shared: { label: "Freigegeben", color: "var(--app-primary)" },
  managed: { label: "In Verwaltung", color: "#0EA5E9" },
  pending: { label: "Ausstehend", color: "#F59E0B" },
  inactive: { label: "Inaktiv", color: "#0EA5E9" },
  blocked: { label: "Gesperrt", color: "#EF4444" },
  problem: { label: "Problem", color: "#F59E0B" },
};
const BuddyBadge = ({ status }) => { const m = BUDDY_STATUS[status] || BUDDY_STATUS.active; return <span data-testid={`buddy-badge-${status}`} className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: m.color }}>{m.label}</span>; };
const Row = ({ k, v }) => <div className="flex justify-between gap-3 border-b border-gray-100 py-1.5"><span className="text-gray-500">{k}</span><span className="font-medium text-right truncate">{v || "—"}</span></div>;
const rightsLabel = (r) => r === "all" ? "Alle Rechte" : (r === "none" || !r) ? "Nur anzeigen" : Array.isArray(r) ? `${r.length} Felder bearbeitbar` : String(r);

function ShareDialog({ open, onOpenChange, tag, mode, fixedCaretaker, onDone }) {
  // mode: "share" (model 1 or 3) or "transfer" (model 2)
  const isTransfer = mode === "transfer";
  const [email, setEmail] = useState("");
  const [editMode, setEditMode] = useState("none"); // all | none | custom
  const [fields, setFields] = useState([]);
  const [notify, setNotify] = useState("owner"); // owner | caretaker | both
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setEmail(""); setEditMode("none"); setFields([]); setNotify("owner"); } }, [open]);
  const toggleField = (f) => setFields((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);

  const submit = async () => {
    setBusy(true);
    try {
      if (isTransfer) {
        await api.post(`/tags/${tag.id}/transfer`, fixedCaretaker ? { to_user_id: fixedCaretaker } : { to_email: email });
        toast.success("Übertragung angefragt – der Empfänger muss bestätigen");
      } else {
        const editable = editMode === "custom" ? (fields.length ? fields : "none") : editMode;
        const body = { model: fixedCaretaker ? 3 : 1, editable_fields: editable, notify };
        if (fixedCaretaker) body.caretaker_id = fixedCaretaker; else body.caretaker_email = email;
        await api.post(`/tags/${tag.id}/share`, body);
        toast.success("Freigabe erstellt");
      }
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{isTransfer ? "Eigentum übertragen" : "SafeBuddy freigeben"}</DialogTitle>
          <DialogDescription>{tag?.label || tag?.code}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!fixedCaretaker && (
            <input data-testid="share-email" type="email" className="sb-input" placeholder="E-Mail des Empfängers" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
          {!isTransfer && (
            <>
              <div>
                <p className="font-body text-sm font-medium mb-1.5">Bearbeitungsrechte des Betreuers</p>
                <div className="flex gap-2">
                  {[["none", "Keine"], ["all", "Alle"], ["custom", "Bestimmte"]].map(([v, l]) => (
                    <button key={v} data-testid={`share-editable-${v}`} onClick={() => setEditMode(v)} className={`flex-1 py-2 rounded-xl border-2 border-gray-800 text-sm font-heading ${editMode === v ? "text-white" : "bg-white text-gray-700"}`} style={editMode === v ? { background: "var(--app-primary)" } : {}}>{l}</button>
                  ))}
                </div>
                {editMode === "custom" && (
                  <div className="grid grid-cols-2 gap-1.5 mt-2" data-testid="share-field-list">
                    {FIELD_OPTIONS.map(([f, l]) => (
                      <label key={f} className="flex items-center gap-2 text-sm font-body py-1 cursor-pointer">
                        <input data-testid={`share-field-${f}`} type="checkbox" className="h-4 w-4 accent-[var(--app-primary)]" checked={fields.includes(f)} onChange={() => toggleField(f)} />
                        {l}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="font-body text-sm font-medium mb-1.5">Scan-Benachrichtigungen</p>
                <div className="flex gap-2">
                  {[["owner", "Nur Inhaber"], ["caretaker", "Nur Betreuer"], ["both", "Beide"]].map(([v, l]) => (
                    <button key={v} data-testid={`share-notify-${v}`} onClick={() => setNotify(v)} className={`flex-1 py-2 rounded-xl border-2 border-gray-800 text-xs font-heading ${notify === v ? "text-white" : "bg-white text-gray-700"}`} style={notify === v ? { background: "var(--app-primary)" } : {}}>{l}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          {isTransfer && <p className="font-body text-sm text-gray-600 rounded-xl border-2 border-red-400 bg-red-50 px-3 py-2">Der Empfänger muss die Übertragung bestätigen. Erst danach hast du keinen Zugriff mehr.</p>}
          <button data-testid="share-submit" onClick={submit} disabled={busy || (!fixedCaretaker && !email.trim())}
            className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: isTransfer ? "#EF4444" : "var(--app-primary)" }}>
            {busy ? "…" : isTransfer ? "Übertragung anfragen" : "Freigeben"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Groups() {
  const { groupsEnabled, user } = useApp();
  const isDesktop = useIsDesktop();
  const [viewMode, setViewModeState] = useState(() => localStorage.getItem("sb_groups_view") || (isDesktop ? "full" : "simple"));
  const setViewMode = (m) => { localStorage.setItem("sb_groups_view", m); setViewModeState(m); };
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState(null);
  const [dash, setDash] = useState(null);
  const [myTags, setMyTags] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", kind: "standard", start_at: "", end_at: "", auto_extend: false, auto_archive: true, auto_delete_days: "" });
  const [inviteEmail, setInviteEmail] = useState("");
  const [share, setShare] = useState(null); // { tag, mode, fixedCaretaker }
  const [incoming, setIncoming] = useState([]);
  const [tagReqs, setTagReqs] = useState([]);
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fStatus, setFStatus] = useState("all");
  const [fKind, setFKind] = useState("all");
  const [confirmDel, setConfirmDel] = useState(false);
  const [notes, setNotes] = useState([]);
  const loadNotes = useCallback(async () => { try { const r = await api.get("/notifications"); setNotes(r.data.filter((n) => !n.read)); } catch { /* noop */ } }, []);
  const dismissNote = async (id) => { try { await api.post(`/notifications/${id}/read`); setNotes((prev) => prev.filter((n) => n.id !== id)); } catch { /* noop */ } };
  const [buddies, setBuddies] = useState([]);
  const [buddyQuery, setBuddyQuery] = useState("");
  const [buddyStatus, setBuddyStatus] = useState("all");
  const respondInvite = async (iid, accept) => {
    try {
      const r = await api.post(`/group-invites/${iid}/${accept ? "accept" : "decline"}`);
      toast.success(accept ? `Beigetreten: ${r.data.name || "Gruppe"}` : "Einladung abgelehnt");
      loadIncoming(); loadGroups();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const [selBuddy, setSelBuddy] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const loadBuddies = useCallback(async (g) => {
    if (!g || !g.is_leader) { setBuddies([]); return; }
    try { const r = await api.get(`/groups/${g.id}/buddies`); setBuddies(r.data); } catch { setBuddies([]); }
  }, []);

  const loadIncoming = useCallback(async () => {
    try { const r = await api.get("/transfers/incoming"); setIncoming(r.data); } catch { /* noop */ }
    try { const r2 = await api.get("/group-tag-requests/incoming"); setTagReqs(r2.data); } catch { /* noop */ }
    try { const r3 = await api.get("/group-invites/incoming"); setInvites(r3.data); } catch { /* noop */ }
  }, []);

  const loadGroups = useCallback(async () => {
    try { const r = await api.get("/groups/mine"); setGroups(r.data); } catch { /* noop */ }
  }, []);
  const loadTags = useCallback(async () => {
    try { const r = await api.get("/tags"); setMyTags(r.data); } catch { /* noop */ }
  }, []);
  const loadDash = useCallback(async (g) => {
    if (!g) { setDash(null); return; }
    if (g.is_leader) { try { const r = await api.get(`/groups/${g.id}/dashboard`); setDash(r.data); } catch { setDash(null); } }
    else setDash(null);
  }, []);

  useEffect(() => { loadGroups(); loadTags(); loadIncoming(); loadNotes(); }, [loadGroups, loadTags, loadIncoming, loadNotes]);
  useEffect(() => { loadDash(sel); loadBuddies(sel); }, [sel, loadDash, loadBuddies]);

  const respondTransfer = async (tid, accept) => {
    try {
      await api.post(`/transfers/${tid}/${accept ? "accept" : "decline"}`);
      toast.success(accept ? "Übertragung angenommen" : "Übertragung abgelehnt");
      loadIncoming(); loadTags(); loadDash(sel);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sel) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/groups/${sel.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSel({ ...sel, logo_path: r.data.logo_path });
      toast.success("Logo aktualisiert");
      loadGroups();
    } catch { toast.error("Fehler beim Hochladen"); }
  };

  const createGroup = async () => {
    if (!form.name.trim()) return;
    if (form.kind === "event" && (!form.start_at || !form.end_at)) { toast.error("Bitte Start- und Enddatum angeben"); return; }
    setBusy(true);
    try {
      const payload = { name: form.name, description: form.description, kind: form.kind };
      if (form.kind === "event") {
        payload.start_at = new Date(form.start_at).toISOString();
        payload.end_at = new Date(form.end_at).toISOString();
        payload.auto_extend = form.auto_extend;
        payload.auto_archive = form.auto_archive;
        payload.auto_delete_days = form.auto_delete_days ? Number(form.auto_delete_days) : null;
      }
      const r = await api.post("/groups", payload);
      toast.success("Gruppe erstellt");
      setForm({ name: "", description: "", kind: "standard", start_at: "", end_at: "", auto_extend: false, auto_archive: true, auto_delete_days: "" });
      await loadGroups(); setSel(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  const invite = async () => {
    if (!inviteEmail.trim()) return;
    try { await api.post(`/groups/${sel.id}/invite`, { email: inviteEmail }); toast.success("Einladung gesendet"); setInviteEmail(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const removeMember = async (uid) => {
    try { await api.delete(`/groups/${sel.id}/members/${uid}`); toast.success("Mitglied entfernt"); const r = await api.get(`/groups/${sel.id}`); setSel(r.data); loadDash(r.data); } catch { toast.error("Fehler"); }
  };
  const revoke = async (tagId, shareId) => {
    try { await api.delete(`/tags/${tagId}/share/${shareId}`); toast.success("Freigabe widerrufen"); loadDash(sel); } catch { toast.error("Fehler"); }
  };
  const archiveGroup = async (archive) => {
    try { const r = await api.post(`/groups/${sel.id}/${archive ? "archive" : "reactivate"}`); toast.success(archive ? "Gruppe archiviert" : "Gruppe reaktiviert"); setSel(r.data); loadGroups(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const deleteGroup = async () => {
    try { await api.delete(`/groups/${sel.id}`); toast.success("Gruppe gelöscht"); setConfirmDel(false); setSel(null); loadGroups(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const canManage = (g) => g.is_leader || (user && user.is_admin);

  if (!groupsEnabled) {
    return (
      <Shell back="/dashboard">
        <div className="pt-24 text-center" data-testid="groups-disabled">
          <UsersThree size={48} weight="duotone" className="mx-auto text-gray-400" />
          <p className="mt-3 font-heading text-gray-500">Die Gruppen-Funktion ist derzeit nicht verfügbar.</p>
        </div>
      </Shell>
    );
  }

  const Badge = ({ status }) => { const m = STATUS_META[status] || STATUS_META.active; return <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white" style={{ background: m.color }} data-testid={`buddy-status-${status}`}>{m.label}</span>; };

  const activeGroups = groups.filter((g) => !g.archived);
  const archivedGroups = groups.filter((g) => g.archived);
  const shownGroups = activeGroups.filter((g) => (fStatus === "all" || g.status === fStatus) && (fKind === "all" || g.kind === fKind));

  return (
    <Shell back="/dashboard">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="groups-page">
        <h1 className="text-3xl font-heading font-semibold text-gray-900 flex items-center gap-2"><UsersThree size={30} weight="duotone" />Gruppen</h1>

        <FamilyManager />

        <PeopleManager variant="groups" />

        {notes.length > 0 && (
          <div className="space-y-2" data-testid="group-notifications">
            {notes.map((n) => (
              <div key={n.id} data-testid={`note-${n.id}`} className="sb-card p-3 flex items-start justify-between gap-2" style={{ borderColor: "#F59E0B" }}>
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><CalendarBlank size={16} weight="bold" />{n.title}</p>
                  <p className="font-body text-xs text-gray-600 mt-0.5">{n.body}</p>
                </div>
                <button data-testid={`note-dismiss-${n.id}`} onClick={() => dismissNote(n.id)} className="text-gray-400 shrink-0"><X size={16} /></button>
              </div>
            ))}
          </div>
        )}

        {invites.length > 0 && (
          <div className="space-y-2" data-testid="group-invites">
            {invites.map((iv) => (
              <div key={iv.id} data-testid={`invite-${iv.id}`} className="sb-card p-4" style={{ borderColor: "var(--app-primary)" }}>
                <p className="font-heading font-semibold flex items-center gap-2"><UsersThree size={18} weight="bold" />Neue Gruppeneinladung</p>
                <p className="font-body text-sm text-gray-600 mt-1">Du wurdest von <strong>{iv.leader_name || "einem Leiter"}</strong> zur Gruppe <strong>{iv.group_name}</strong> eingeladen.</p>
                <p className="font-body text-xs text-gray-500 mt-1">{iv.kind === "event" ? "Event" : "Standard"} · {iv.member_count} Mitglieder{iv.kind === "event" && (iv.start_at || iv.end_at) ? ` · ${fmtRange(iv.start_at, iv.end_at)}` : ""}</p>
                <div className="flex gap-2 mt-2">
                  <button data-testid={`invite-accept-${iv.id}`} onClick={() => respondInvite(iv.id, true)} className="sb-btn flex-1 py-2 text-white text-sm font-medium flex items-center justify-center gap-1" style={{ background: "var(--app-primary)" }}><Check size={16} />Annehmen</button>
                  <button data-testid={`invite-decline-${iv.id}`} onClick={() => respondInvite(iv.id, false)} className="sb-btn flex-1 py-2 bg-white text-sm font-medium flex items-center justify-center gap-1"><X size={16} />Ablehnen</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tagReqs.length > 0 && (
          <div className="space-y-2" data-testid="group-tag-requests">
            {tagReqs.map((r) => (
              <div key={r.id} data-testid={`tagreq-${r.id}`} className="sb-card p-4" style={{ borderColor: "var(--app-primary)" }}>
                <p className="font-heading font-semibold">Gruppenaufnahme angefragt</p>
                <p className="font-body text-sm text-gray-600 mt-1"><strong>{r.leader_name}</strong> möchte „{r.label || "SafeBuddy"}" in die Gruppe <strong>{r.group_name}</strong> aufnehmen.</p>
                <div className="flex gap-2 mt-2">
                  <button data-testid={`accept-tagreq-${r.id}`} onClick={async () => { try { await api.post(`/group-tag-requests/${r.id}/accept`); toast.success("Aufnahme bestätigt"); loadIncoming(); } catch { toast.error("Fehler"); } }} className="sb-btn flex-1 py-2 text-white text-sm font-medium" style={{ background: "var(--app-primary)" }}>Bestätigen</button>
                  <button data-testid={`decline-tagreq-${r.id}`} onClick={async () => { try { await api.post(`/group-tag-requests/${r.id}/decline`); toast.success("Abgelehnt"); loadIncoming(); } catch { toast.error("Fehler"); } }} className="sb-btn flex-1 py-2 bg-white text-sm font-medium">Ablehnen</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {incoming.length > 0 && (
          <div className="space-y-2" data-testid="incoming-transfers">
            {incoming.map((tr) => (
              <div key={tr.id} data-testid={`incoming-${tr.id}`} className="sb-card p-4" style={{ borderColor: "var(--app-primary)" }}>
                <p className="font-heading font-semibold flex items-center gap-2"><ArrowsLeftRight size={18} weight="bold" />Eigentumsübertragung</p>
                <p className="font-body text-sm text-gray-600 mt-1"><strong>{tr.from_name}</strong> möchte dir „{tr.label || "SafeBuddy"}" übertragen.</p>
                <div className="flex gap-2 mt-2">
                  <button data-testid={`accept-transfer-${tr.id}`} onClick={() => respondTransfer(tr.id, true)} className="sb-btn flex-1 py-2 text-white text-sm font-medium flex items-center justify-center gap-1" style={{ background: "var(--app-primary)" }}><Check size={16} />Annehmen</button>
                  <button data-testid={`decline-transfer-${tr.id}`} onClick={() => respondTransfer(tr.id, false)} className="sb-btn flex-1 py-2 bg-white text-sm font-medium flex items-center justify-center gap-1"><X size={16} />Ablehnen</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isDesktop && (
          <div className="flex items-center gap-2 rounded-2xl border-2 border-gray-800 bg-white p-1" data-testid="groups-view-toggle">
            <button data-testid="groups-view-simple" onClick={() => setViewMode("simple")} className={`flex-1 py-2 rounded-xl text-sm font-heading flex items-center justify-center gap-1.5 ${viewMode === "simple" ? "text-white" : "text-gray-600"}`} style={viewMode === "simple" ? { background: "var(--app-primary)" } : {}}><DeviceMobile size={16} weight="bold" />Einfach</button>
            <button data-testid="groups-view-full" onClick={() => setViewMode("full")} className={`flex-1 py-2 rounded-xl text-sm font-heading flex items-center justify-center gap-1.5 ${viewMode === "full" ? "text-white" : "text-gray-600"}`} style={viewMode === "full" ? { background: "var(--app-primary)" } : {}}><DesktopTower size={16} weight="bold" />Vollständig</button>
          </div>
        )}

        {!sel && (
          <>
            <div className="sb-card p-5 space-y-3" data-testid="create-group-card">
              <h2 className="font-heading font-semibold flex items-center gap-2"><Plus size={20} weight="bold" />Gruppe erstellen</h2>
              <div className="flex gap-2" data-testid="group-kind-toggle">
                <button data-testid="kind-standard" onClick={() => setForm({ ...form, kind: "standard" })} className={`flex-1 py-2 rounded-xl border-2 border-gray-800 text-sm font-heading flex items-center justify-center gap-1.5 ${form.kind === "standard" ? "text-white" : "bg-white text-gray-700"}`} style={form.kind === "standard" ? { background: "var(--app-primary)" } : {}}><UsersThree size={16} weight="bold" />Standard</button>
                <button data-testid="kind-event" onClick={() => setForm({ ...form, kind: "event" })} className={`flex-1 py-2 rounded-xl border-2 border-gray-800 text-sm font-heading flex items-center justify-center gap-1.5 ${form.kind === "event" ? "text-white" : "bg-white text-gray-700"}`} style={form.kind === "event" ? { background: "var(--app-strong)" } : {}}><CalendarBlank size={16} weight="bold" />Event</button>
              </div>
              <input data-testid="group-name" className="sb-input" placeholder="Gruppenname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <textarea data-testid="group-desc" className="sb-input" rows={2} placeholder="Beschreibung (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              {form.kind === "event" && (
                <div className="space-y-3 rounded-xl bg-gray-50 border border-gray-200 p-3" data-testid="event-fields">
                  <div className="grid grid-cols-2 gap-2">
                    <label className="font-body text-xs text-gray-600">Start<input data-testid="event-start" type="datetime-local" className="sb-input mt-1" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></label>
                    <label className="font-body text-xs text-gray-600">Ende<input data-testid="event-end" type="datetime-local" className="sb-input mt-1" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></label>
                  </div>
                  <label className="flex items-center gap-2 font-body text-sm cursor-pointer"><input data-testid="event-auto-extend" type="checkbox" className="h-4 w-4 accent-[var(--app-primary)]" checked={form.auto_extend} onChange={(e) => setForm({ ...form, auto_extend: e.target.checked })} />Automatisch verlängern</label>
                  <label className="flex items-center gap-2 font-body text-sm cursor-pointer"><input data-testid="event-auto-archive" type="checkbox" className="h-4 w-4 accent-[var(--app-primary)]" checked={form.auto_archive} onChange={(e) => setForm({ ...form, auto_archive: e.target.checked })} />Nach Ende automatisch archivieren</label>
                  <label className="font-body text-xs text-gray-600 block">Automatisch löschen nach Ende
                    <select data-testid="event-auto-delete" className="sb-input mt-1" value={form.auto_delete_days} onChange={(e) => setForm({ ...form, auto_delete_days: e.target.value })}>
                      <option value="">Nie</option>
                      <option value="7">Nach 7 Tagen</option>
                      <option value="30">Nach 30 Tagen</option>
                    </select>
                  </label>
                </div>
              )}
              <button data-testid="create-group-submit" onClick={createGroup} disabled={busy || !form.name.trim()} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>Erstellen</button>
            </div>

            <div className="sb-card p-5 space-y-3">
              <h2 className="font-heading font-semibold flex items-center gap-2"><SignIn size={20} weight="bold" />Einer Gruppe beitreten</h2>
              <a href="/groups/join" data-testid="goto-join" className="sb-btn inline-block w-full text-center py-3 bg-white font-medium">Mit Beitrittscode beitreten</a>
            </div>

            {(activeGroups.length > 0 || archivedGroups.length > 0) && (
              <div className="space-y-3" data-testid="my-groups">
                <h2 className="font-heading font-semibold text-gray-700">Meine Gruppen</h2>
                <div className="flex flex-wrap items-center gap-1.5" data-testid="group-filters">
                  {[["all", "Alle"], ["active", "Aktiv"], ["upcoming", "Event"], ["expired", "Abgelaufen"]].map(([v, l]) => (
                    <button key={v} data-testid={`filter-status-${v}`} onClick={() => setFStatus(v)} className={`text-xs font-heading px-2.5 py-1 rounded-full border-2 border-gray-800 ${fStatus === v ? "text-white" : "bg-white text-gray-600"}`} style={fStatus === v ? { background: "var(--app-primary)" } : {}}>{l}</button>
                  ))}
                  <span className="w-px h-4 bg-gray-300 mx-0.5" />
                  {[["all", "Alle Arten"], ["standard", "Standard"], ["event", "Events"]].map(([v, l]) => (
                    <button key={v} data-testid={`filter-kind-${v}`} onClick={() => setFKind(v)} className={`text-xs font-heading px-2.5 py-1 rounded-full border-2 border-gray-800 ${fKind === v ? "text-white" : "bg-white text-gray-600"}`} style={fKind === v ? { background: "var(--app-strong)" } : {}}>{l}</button>
                  ))}
                </div>
                {shownGroups.length === 0 && <p className="font-body text-sm text-gray-400">Keine Gruppen für diesen Filter.</p>}
                {shownGroups.map((g) => <GroupCard key={g.id} g={g} onClick={() => setSel(g)} />)}

                {archivedGroups.length > 0 && (
                  <div className="pt-2 space-y-2" data-testid="group-archive">
                    <h3 className="font-heading text-sm font-semibold text-gray-500 flex items-center gap-1.5"><Archive size={16} weight="bold" />Archiv ({archivedGroups.length})</h3>
                    {archivedGroups.map((g) => <GroupCard key={g.id} g={g} onClick={() => setSel(g)} />)}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {sel && (
          <div className="space-y-4" data-testid="group-detail">
            <button data-testid="group-back-list" onClick={() => setSel(null)} className="font-body text-sm text-gray-500 underline">← Alle Gruppen</button>
            <div className="sb-card p-5">
              <div className="flex items-center gap-3">
                {sel.logo_path ? (
                  <img src={sel.logo_path} alt="" data-testid="group-logo" className="w-14 h-14 rounded-xl object-cover border-2 border-gray-800" />
                ) : (
                  <div className="w-14 h-14 rounded-xl border-2 border-gray-800 flex items-center justify-center" style={{ background: "var(--app-primary)" }}><UsersThree size={26} weight="duotone" className="text-white" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><h2 className="text-xl font-heading font-semibold truncate">{sel.name}</h2><GStatus status={sel.status} /></div>
                  {sel.description && <p className="font-body text-sm text-gray-600 mt-0.5">{sel.description}</p>}
                  {sel.kind === "event" && (sel.start_at || sel.end_at) && <p className="font-body text-xs text-gray-400 mt-0.5 flex items-center gap-1"><CalendarBlank size={12} weight="bold" />{fmtRange(sel.start_at, sel.end_at)}</p>}
                </div>
              </div>
              {sel.is_leader && (
                <label data-testid="group-logo-upload" className="mt-3 inline-flex items-center gap-1.5 text-sm font-heading text-gray-600 cursor-pointer">
                  <ImageIcon size={16} weight="bold" /> Logo {sel.logo_path ? "ändern" : "hochladen"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                </label>
              )}
              {sel.is_leader && sel.join_code && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="font-body text-sm text-gray-500">Beitrittscode:</span>
                  <span className="font-heading font-bold tracking-widest" data-testid="group-join-code-display">{sel.join_code}</span>
                  <button data-testid="copy-join-code" onClick={() => { copyText(sel.join_code); toast.success("Kopiert"); }} className="p-1.5 rounded-lg border-2 border-gray-800 bg-white"><Copy size={16} /></button>
                </div>
              )}
              {canManage(sel) && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100" data-testid="group-actions">
                  {sel.status === "inactive" ? (
                    <button data-testid="group-reactivate" onClick={() => archiveGroup(false)} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1.5"><ArrowCounterClockwise size={16} weight="bold" />Reaktivieren</button>
                  ) : (
                    <button data-testid="group-archive-btn" onClick={() => archiveGroup(true)} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1.5"><Archive size={16} weight="bold" />Archivieren</button>
                  )}
                  <button data-testid="group-delete-btn" onClick={() => setConfirmDel(true)} className="sb-btn px-3 py-2 text-sm font-medium flex items-center gap-1.5 text-white" style={{ background: "#EF4444" }}><Trash size={16} weight="bold" />Löschen</button>
                </div>
              )}
            </div>

            {sel.is_leader && sel.status !== "inactive" && (
              <div className="sb-card p-5 space-y-2" data-testid="group-invite-card">
                <h3 className="font-heading font-semibold">Mitglied einladen</h3>
                <div className="flex gap-2">
                  <input data-testid="invite-email" type="email" className="sb-input" placeholder="E-Mail" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                  <button data-testid="invite-submit" onClick={invite} disabled={!inviteEmail.trim()} className="sb-btn px-4 bg-white font-medium disabled:opacity-50 shrink-0">Einladen</button>
                </div>
              </div>
            )}

            <div className="sb-card p-4">
              <h3 className="font-heading font-semibold mb-2">Mitglieder ({sel.member_count})</h3>
              {sel.members.map((m) => (
                <div key={m.user_id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                  <div><span className="font-body text-sm">{m.name}</span> <span className="text-[10px] uppercase text-gray-400">{m.role === "leader" ? "Leiter" : "Mitglied"}</span></div>
                  {sel.is_leader && m.role !== "leader" && <button data-testid={`remove-member-${m.user_id}`} onClick={() => removeMember(m.user_id)} className="text-red-500"><Trash size={16} /></button>}
                </div>
              ))}
            </div>

            {sel.is_leader && dash && (
              <div className="space-y-3" data-testid="group-dashboard">
                {(isDesktop || viewMode === "full") && (
                  <div className="sb-card p-4 flex items-center justify-between">
                    <span className="font-heading font-semibold">Gruppen-Scans gesamt</span>
                    <span className="text-2xl font-heading font-bold" style={{ color: "var(--app-strong)" }} data-testid="group-scans-total">{dash.group_scans}</span>
                  </div>
                )}
                <h3 className="font-heading font-semibold text-gray-700">SafeBuddys</h3>
                {dash.items.length === 0 && <p className="font-body text-sm text-gray-500">Noch keine SafeBuddys.</p>}
                {dash.items.map((it, i) => (
                  <div key={i} data-testid={`dash-item-${i}`} className="sb-card p-4">
                    <div className="flex items-center justify-between">
                      <p className="font-heading font-semibold">{it.tag.label || it.tag.code}</p>
                      <Badge status={it.status} />
                    </div>
                    <p className="font-body text-xs text-gray-500 mt-0.5">{it.tag.scans ?? 0} Scans</p>
                    {it.role === "owner" && (
                      <div className="flex gap-2 mt-2">
                        <button data-testid={`share-btn-${i}`} onClick={() => setShare({ tag: it.tag, mode: "share" })} className="sb-btn flex-1 py-2 text-white text-sm font-medium flex items-center justify-center gap-1" style={{ background: "var(--app-primary)" }}><ShareNetwork size={16} />Freigeben</button>
                        <button data-testid={`transfer-btn-${i}`} onClick={() => setShare({ tag: it.tag, mode: "transfer" })} className="sb-btn flex-1 py-2 bg-white text-sm font-medium flex items-center justify-center gap-1"><ArrowsLeftRight size={16} />Übertragen</button>
                      </div>
                    )}
                    {it.role === "caretaker" && (
                      <button data-testid={`revoke-btn-${i}`} onClick={() => revoke(it.tag.id, it.share_id)} className="mt-2 text-red-500 text-sm font-heading flex items-center gap-1"><X size={14} />Verwaltung beenden</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {sel.is_leader && (
              <div className="sb-card p-4" data-testid="group-buddies">
                <h3 className="font-heading font-semibold mb-2">Buddies der Gruppe ({buddies.length})</h3>
                {buddies.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="buddy-filters">
                    <input data-testid="buddy-search" className="sb-input flex-1 min-w-[140px] py-1.5 text-sm" placeholder="Suche Buddy oder Besitzer…" value={buddyQuery} onChange={(e) => setBuddyQuery(e.target.value)} />
                    {[["all", "Alle"], ["active", "Aktiv"], ["shared", "Freigegeben"], ["managed", "Verwaltet"], ["blocked", "Gesperrt"]].map(([v, l]) => (
                      <button key={v} data-testid={`buddy-filter-${v}`} onClick={() => setBuddyStatus(v)} className={`text-xs font-heading px-2 py-1 rounded-full border-2 border-gray-800 ${buddyStatus === v ? "text-white" : "bg-white text-gray-600"}`} style={buddyStatus === v ? { background: "var(--app-strong)" } : {}}>{l}</button>
                    ))}
                  </div>
                )}
                {(() => {
                  const q = buddyQuery.trim().toLowerCase();
                  const shown = buddies.filter((b) => (buddyStatus === "all" || b.status === buddyStatus) && (!q || (b.label || "").toLowerCase().includes(q) || (b.owner_name || "").toLowerCase().includes(q) || (b.code || "").toLowerCase().includes(q)));
                  if (buddies.length === 0) return <p className="font-body text-sm text-gray-500">Noch keine Buddies zugeordnet.</p>;
                  if (shown.length === 0) return <p className="font-body text-sm text-gray-400">Keine Buddies für Suche/Filter.</p>;
                  return shown.map((b) => (
                    <button key={b.tag_id} data-testid={`buddy-row-${b.tag_id}`} onClick={() => { setSelBuddy(b); setScanOpen(false); }} className="w-full text-left flex items-center justify-between gap-2 py-2 border-b border-gray-100 last:border-0 active:opacity-70">
                      <div className="min-w-0">
                        <p className="font-body text-sm font-medium truncate">{b.label}</p>
                        <p className="font-body text-xs text-gray-500 truncate">{b.owner_name} · {b.type} · {b.role === "owner" ? "Besitzer" : "verwaltet"}</p>
                      </div>
                      <BuddyBadge status={b.status} />
                    </button>
                  ));
                })()}
              </div>
            )}

            {!sel.is_leader && (
              <div className="sb-card p-5 space-y-3" data-testid="member-share-card">
                <h3 className="font-heading font-semibold">Meinen SafeBuddy dem Leiter freigeben</h3>
                <p className="font-body text-xs text-gray-500">Du behältst alle Rechte und kannst die Freigabe jederzeit widerrufen.</p>
                {myTags.length === 0 && <p className="font-body text-sm text-gray-500">Du hast noch keine SafeBuddys.</p>}
                {myTags.map((tg) => (
                  <div key={tg.id} className="flex items-center justify-between border-2 border-gray-800 rounded-2xl px-3 py-2 bg-white">
                    <span className="font-body text-sm">{tg.label || tg.code}</span>
                    <button data-testid={`member-share-${tg.id}`} onClick={() => setShare({ tag: tg, mode: "share", fixedCaretaker: sel.leader_id })} className="sb-btn px-3 py-1.5 text-white text-sm font-medium" style={{ background: "var(--app-primary)" }}>Freigeben</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>

      {share && (
        <ShareDialog open={!!share} onOpenChange={(v) => !v && setShare(null)} tag={share.tag} mode={share.mode} fixedCaretaker={share.fixedCaretaker} onDone={() => { loadDash(sel); loadGroups(); }} />
      )}

      {selBuddy && (
        <Dialog open={!!selBuddy} onOpenChange={(v) => { if (!v) { setSelBuddy(null); setScanOpen(false); } }}>
          <DialogContent data-testid="buddy-detail-dialog" className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">{selBuddy.label}<BuddyBadge status={selBuddy.status} /></DialogTitle>
              <DialogDescription>SafeBuddy-Details</DialogDescription>
            </DialogHeader>
            <div className="text-sm font-body">
              <Row k="Besitzer" v={selBuddy.owner_name} />
              <Row k="Typ" v={selBuddy.type} />
              <Row k="NFC-Tag" v={selBuddy.code} />
              <Row k="Status" v={(BUDDY_STATUS[selBuddy.status] || {}).label} />
              <Row k="Rolle" v={selBuddy.role === "owner" ? "Besitzer" : "Betreuer (verwaltet)"} />
              <Row k="Berechtigungen" v={rightsLabel(selBuddy.rights)} />
              <Row k="Zugeordnet am" v={selBuddy.assigned_at ? new Date(selBuddy.assigned_at).toLocaleDateString("de-DE") : "—"} />
            </div>
            <div className="mt-3 space-y-2">
              <button data-testid="buddy-scan-preview" onClick={() => setScanOpen((v) => !v)} className="sb-btn w-full py-2.5 text-white font-medium flex items-center justify-center gap-1.5" style={{ background: "var(--app-primary)" }}><Eye size={16} weight="bold" />{scanOpen ? "Vorschau ausblenden" : "Scan-Vorschau"}</button>
              <a data-testid="buddy-open-scan" href={`/t/${selBuddy.code}?preview=true`} target="_blank" rel="noreferrer" className="sb-btn w-full block text-center py-2.5 bg-white font-medium flex items-center justify-center gap-1.5"><ArrowSquareOut size={16} weight="bold" />Scan-Seite öffnen</a>
            </div>
            {scanOpen && (
              <div className="mt-3" data-testid="scan-preview-frame">
                <p className="font-body text-xs text-gray-500 mb-1">Scan-Vorschau – so sieht ein Finder die Seite (keine Verwaltungsdaten):</p>
                <div className="rounded-xl border-2 border-gray-800 overflow-hidden bg-white" style={{ height: 420 }}>
                  <iframe title="Scan-Vorschau" src={`/t/${selBuddy.code}?preview=true`} className="w-full h-full" />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {sel && (
        <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
          <DialogContent data-testid="confirm-delete-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Gruppe wirklich löschen?</DialogTitle>
              <DialogDescription>„{sel.name}" wird dauerhaft gelöscht. Alle Mitglieder werden entfernt. Die SafeBuddys bleiben bei ihren Besitzern erhalten.</DialogDescription>
            </DialogHeader>
            <div className="flex gap-2">
              <button data-testid="confirm-delete-cancel" onClick={() => setConfirmDel(false)} className="sb-btn flex-1 py-2.5 bg-white font-medium">Abbrechen</button>
              <button data-testid="confirm-delete-yes" onClick={deleteGroup} className="sb-btn flex-1 py-2.5 text-white font-medium" style={{ background: "#EF4444" }}>Endgültig löschen</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Shell>
  );
}
